/**
 * Custom React hooks for data fetching.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';

import {
    searchApi,
    projectsApi,
    documentsApi,
    ingestionApi,
    priceSetsApi,
    authApi,
    aiApi,
    agenciesApi,
    feedsApi,
    importedApi,
    pursuitsApi,
    subscriptionsApi,
    complianceApi,
} from './api';

// --- Compliance Hooks ---
export function useComplianceItems(pursuitId: number, options?: any) {
    return useQuery({
        queryKey: ['compliance', pursuitId],
        queryFn: () => complianceApi.list(pursuitId),
        enabled: !!pursuitId,
        staleTime: 1000 * 60 * 5,
        ...options,
    });
}

export function useCreateComplianceItems() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ pursuitId, items }: { pursuitId: number; items: any[] }) =>
            complianceApi.create(pursuitId, items),
        onSuccess: (_, { pursuitId }) => {
            queryClient.invalidateQueries({ queryKey: ['compliance', pursuitId] });
        },
    });
}

export function useUpdateComplianceItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            complianceApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['compliance'] });
        },
    });
}

export function useDeleteComplianceItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => complianceApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['compliance'] });
        },
    });
}

// --- Agencies Hooks ---
export function useAgencies(options?: any) {
    return useQuery<any>({
        queryKey: ['agencies'],
        queryFn: agenciesApi.list,
        staleTime: 1000 * 60 * 5,
        ...options,
    });
}

export function useRunAgency() {
    return useMutation({
        mutationFn: agenciesApi.run,
    });
}

// --- Feeds Hooks ---
export function useFeeds(params?: { agency_id?: number }) {
    return useQuery({
        queryKey: ['feeds', params],
        queryFn: () => feedsApi.list(params),
        staleTime: 1000 * 60 * 5,
    });
}

export function useRunFeed() {
    return useMutation({
        mutationFn: feedsApi.run,
    });
}

// --- Imported Items Hooks ---
export function useImportedItems(params: {
    type: string;
    status?: string;
    page?: number;
    limit?: number;
    search?: string;
    agency_ids?: number[];
    min_price?: number;
    max_price?: number;
    date_from?: string;
    date_to?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
}) {
    return useQuery({
        queryKey: ['importedItems', params],
        queryFn: () => importedApi.list(params),
        staleTime: 1000 * 30,
    });
}

export function useMoveToPipeline() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, notes }: { id: number; notes?: string }) =>
            importedApi.move(id, { notes }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['importedItems'] });
            queryClient.invalidateQueries({ queryKey: ['pursuits'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
            queryClient.invalidateQueries({ queryKey: ['pipelineStats'] });
        },
    });
}

export function useDeleteImportedItem() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => importedApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['importedItems'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
        },
    });
}

export function useBulkDeleteImportedItems() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (ids: number[]) => importedApi.bulkDelete(ids),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['importedItems'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
        },
    });
}

export function useBulkMoveToPursuits() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (ids: number[]) => importedApi.bulkMove(ids),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['importedItems'] });
            queryClient.invalidateQueries({ queryKey: ['pursuits'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
            queryClient.invalidateQueries({ queryKey: ['pipelineStats'] });
        },
    });
}

// --- Pursuits Hooks ---
export function usePursuitDashboard() {
    return useQuery({
        queryKey: ['pursuits', 'dashboard'],
        queryFn: pursuitsApi.getDashboardKanban,
        staleTime: 1000 * 30, // Poll every 30s
        refetchInterval: 30000,
    });
}

export function usePursuits(params?: { stage?: string }) {
    return useQuery({
        queryKey: ['pursuits', params],
        queryFn: () => pursuitsApi.list(params),
        staleTime: 1000 * 30,
    });
}

export function usePursuit(id: number) {
    return useQuery({
        queryKey: ['pursuit', id],
        queryFn: () => pursuitsApi.get(id),
        enabled: !!id,
    });
}

export function useUpdatePursuit() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            pursuitsApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pursuits'] });
        },
    });
}

export function useReturnToImported() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: pursuitsApi.return,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pursuits'] });
            queryClient.invalidateQueries({ queryKey: ['importedItems'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
            queryClient.invalidateQueries({ queryKey: ['pipelineStats'] });
        },
    });
}

// --- Auth hooks ---
export function useCurrentUser() {
    const { isSignedIn } = useAuth();

    return useQuery({
        queryKey: ['currentUser'],
        queryFn: authApi.me,
        enabled: isSignedIn,
        staleTime: 1000 * 60 * 10, // 10 minutes
    });
}

// --- Search hooks ---
export function useSearch(query: string, filters?: Record<string, any>) {
    return useQuery({
        queryKey: ['search', query, filters],
        queryFn: () => searchApi.search(query, filters),
        enabled: query.length >= 2,
        staleTime: 1000 * 60, // 1 minute
    });
}

// --- Projects hooks ---
export function useProjects(params?: { page?: number; pageSize?: number; agency?: string; status?: string; opportunity_type?: string }) {
    return useQuery({
        queryKey: ['projects', params],
        queryFn: () => projectsApi.list(params),
        staleTime: 1000 * 60 * 5,
    });
}

export function useProject(id: number) {
    return useQuery({
        queryKey: ['project', id],
        queryFn: () => projectsApi.get(id),
        enabled: !!id,
    });
}

export function useCreateProject() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: projectsApi.create,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });
}

export function useDeleteProject() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: projectsApi.delete,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });
}

export function useProjectBidItems(projectId: number, params?: { page?: number; pageSize?: number }) {
    return useQuery({
        queryKey: ['projectBidItems', projectId, params],
        queryFn: () => projectsApi.getBidItems(projectId, params),
        enabled: !!projectId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
}

// --- Documents hooks ---
export function useDocuments(params?: { projectId?: number; page?: number }) {
    return useQuery({
        queryKey: ['documents', params],
        queryFn: () => documentsApi.list(params),
        staleTime: 1000 * 60 * 5,
    });
}

export function useUploadDocument() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ file, metadata }: { file: File; metadata: { projectId?: number; tags?: string[] } }) =>
            documentsApi.upload(file, metadata),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documents'] });
        },
    });
}

// --- Ingestion hooks (Deprecated) ---
export function useIngestionBatches(params?: { status?: string; page?: number }) {
    return useQuery({
        queryKey: ['ingestionBatches', params],
        queryFn: () => ingestionApi.listBatches(params),
        staleTime: 1000 * 30,
    });
}

// --- Price Sets hooks ---
export function usePriceSets() {
    return useQuery({
        queryKey: ['priceSets'],
        queryFn: priceSetsApi.list,
        staleTime: 1000 * 60 * 5,
    });
}

export function usePriceSet(id: number | null) {
    return useQuery({
        queryKey: ['priceSet', id],
        queryFn: () => priceSetsApi.get(id!),
        enabled: !!id,
        staleTime: 1000 * 60 * 2,
    });
}

export function useCreatePriceSet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: priceSetsApi.create,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['priceSets'] });
        },
    });
}

export function useUpdatePriceSet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => priceSetsApi.update(id, data),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['priceSets'] });
            queryClient.invalidateQueries({ queryKey: ['priceSet', id] });
        },
    });
}

export function useDeletePriceSet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => priceSetsApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['priceSets'] });
        },
    });
}

export function useDuplicatePriceSet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => priceSetsApi.duplicate(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['priceSets'] });
        },
    });
}

export function useAddLineItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ setId, data }: { setId: number; data: any }) =>
            priceSetsApi.addLineItem(setId, data),
        onSuccess: (_, { setId }) => {
            queryClient.invalidateQueries({ queryKey: ['priceSet', setId] });
            queryClient.invalidateQueries({ queryKey: ['priceSets'] });
        },
    });
}

export function useUpdateLineItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ setId, itemId, data }: { setId: number; itemId: number; data: any }) =>
            priceSetsApi.updateLineItem(setId, itemId, data),
        onSuccess: (_, { setId }) => {
            queryClient.invalidateQueries({ queryKey: ['priceSet', setId] });
        },
    });
}

export function useDeleteLineItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ setId, itemId }: { setId: number; itemId: number }) =>
            priceSetsApi.deleteLineItem(setId, itemId),
        onSuccess: (_, { setId }) => {
            queryClient.invalidateQueries({ queryKey: ['priceSet', setId] });
            queryClient.invalidateQueries({ queryKey: ['priceSets'] });
        },
    });
}

export function useReorderLineItems() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ setId, orderedIds }: { setId: number; orderedIds: number[] }) =>
            priceSetsApi.reorderLineItems(setId, orderedIds),
        onSuccess: (_, { setId }) => {
            queryClient.invalidateQueries({ queryKey: ['priceSet', setId] });
        },
    });
}

export function useMarketIntel(setId: number | null) {
    return useQuery({
        queryKey: ['marketIntel', setId],
        queryFn: () => priceSetsApi.marketIntel(setId!),
        enabled: !!setId,
        staleTime: 1000 * 60 * 10,
    });
}

export function useExportPriceSet() {
    return useMutation({
        mutationFn: ({ setId, format }: { setId: number; format: 'csv' | 'xlsx' }) =>
            priceSetsApi.export(setId, format),
    });
}

// --- Dashboard stats hook ---
export function useDashboardStats() {
    return useQuery({
        queryKey: ['dashboardStats'],
        queryFn: async () => {
            const [projects, priceSets, imported] = await Promise.all([
                projectsApi.list({ pageSize: 1 }),
                priceSetsApi.list(),
                importedApi.list({ type: 'active', limit: 1 }),
            ]);

            return {
                totalProjects: projects.total || 0,
                totalPriceSets: priceSets.length || 0,
                pendingBatches: imported.total || 0,
            };
        },
        staleTime: 1000 * 60,
    });
}

// --- AI Hooks ---
export function usePipelineStats() {
    return useQuery({
        queryKey: ['pipelineStats'],
        queryFn: aiApi.getPipelineStats,
        staleTime: 1000 * 60,
    });
}

export function usePriceTrends() {
    return useQuery({
        queryKey: ['priceTrends'],
        queryFn: aiApi.getPriceTrends,
        staleTime: 1000 * 60 * 5,
    });
}

export function useAiCompliance(projectId: number) {
    return useQuery({
        queryKey: ['aiCompliance', projectId],
        queryFn: () => aiApi.getCompliance(projectId),
        enabled: !!projectId,
        staleTime: 1000 * 60 * 10,
    });
}

export function useAnalyzeWinFactors() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (projectId: number) => aiApi.analyzeWinFactors(projectId),
        onSuccess: (_, projectId) => {
            queryClient.invalidateQueries({ queryKey: ['aiWinFactors', projectId] });
        },
    });
}

export function useAiChat() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ message, projectId, sessionId, contextText }: { message: string; projectId?: number; sessionId?: number; contextText?: string }) =>
            aiApi.chat(message, projectId, sessionId, contextText),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chatHistory'] });
        }
    });
}

export function useSummarizeProposal() {
    return useMutation({
        mutationFn: (projectId: number) => aiApi.summarize(projectId),
    });
}

export function useChatHistory(params: { project_id?: number; imported_item_id?: number; session_id?: number }) {
    return useQuery({
        queryKey: ['chatHistory', params],
        queryFn: () => aiApi.getChatHistory(params),
        enabled: !!(params.project_id || params.imported_item_id || params.session_id),
        staleTime: 0, // Always get fresh history
    });
}

export function useChatSessions(params: { project_id?: number; imported_item_id?: number }) {
    return useQuery({
        queryKey: ['chatSessions', params],
        queryFn: () => aiApi.getChatSessions(params),
        enabled: !!(params.project_id || params.imported_item_id),
    });
}

export function useCreateChatSession() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: aiApi.createChatSession,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['chatSessions', { project_id: variables.project_id, imported_item_id: variables.imported_item_id }] });
        },
    });
}

export function useRenameChatSession() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sessionId, title }: { sessionId: number; title: string }) => aiApi.renameChatSession(sessionId, title),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chatSessions'] });
        },
    });
}

export function useDeleteChatSession() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: aiApi.deleteChatSession,
        onMutate: async (deletedId: number) => {
            await queryClient.cancelQueries({ queryKey: ['chatSessions'] });
            await queryClient.cancelQueries({ queryKey: ['globalChatSessions'] });

            queryClient.setQueriesData({ queryKey: ['chatSessions'] }, (old: any) => {
                if (!old || !Array.isArray(old)) return old;
                return old.filter((s: any) => s.id !== deletedId);
            });

            queryClient.setQueriesData({ queryKey: ['globalChatSessions'] }, (old: any) => {
                if (!old || !Array.isArray(old)) return old;
                return old.filter((s: any) => s.id !== deletedId);
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chatSessions'] });
            queryClient.invalidateQueries({ queryKey: ['globalChatSessions'] });
        },
    });
}

// Global AI Agent hooks
export function useGlobalChatSessions() {
    return useQuery({
        queryKey: ['globalChatSessions'],
        queryFn: () => aiApi.getGlobalSessions(),
    });
}

export function useCreateGlobalSession() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: aiApi.createGlobalSession,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['globalChatSessions'] });
        },
    });
}
// --- Subscription Hooks ---
export function useSubscriptions() {
    return useQuery({
        queryKey: ['subscriptions'],
        queryFn: subscriptionsApi.list,
        staleTime: 1000 * 60 * 5,
    });
}

export function useSubscribe() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: subscriptionsApi.subscribe,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['importedItems'] });
        },
    });
}

export function useUnsubscribe() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: subscriptionsApi.unsubscribe,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['importedItems'] });
        },
    });
}
